import { useCallback, useEffect, useState } from 'react';
import { countCvs, getLastCvScore, type LastCvScore } from '../services/cv';

/**
 * Données d'en-tête de Carrière : dernière notation persistée et nombre de CV enregistrés.
 *
 * `lastScore` à null = aucune notation à ce jour → la carte affiche 0/100 et invite à en lancer une.
 * Une lecture en échec ne bloque rien : on retombe sur cet état vide, l'écran reste utilisable et
 * toutes ses actions restent accessibles.
 *
 * `refresh` est appelé au retour sur l'écran (focus) : une notation lancée depuis Carrière écrit
 * côté serveur, la carte doit refléter le nouveau score sans redémarrage.
 *
 * `cvCount` à null = nombre INCONNU (lecture en cours, ou ratée sans nombre déjà connu), à ne jamais
 * afficher comme « Aucun CV enregistré » : parti de 0, il le disait pendant chaque chargement, et une
 * lecture ratée y retombait (retour d'essai du 17/10/2026). Une relecture garde le dernier nombre
 * connu jusqu'au nouveau.
 */
export function useCvOverview() {
  const [lastScore, setLastScore] = useState<LastCvScore | null>(null);
  const [cvCount, setCvCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [score, count] = await Promise.all([
      getLastCvScore().catch(() => null),
      countCvs().catch(() => null),
    ]);
    return { score, count };
  }, []);

  const [reloadKey, setReloadKey] = useState(0);
  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void load()
      .then(({ score, count }) => {
        if (!active) return;
        setLastScore(score);
        if (count !== null) setCvCount(count);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [load, reloadKey]);

  return { lastScore, cvCount, loading, refresh };
}
